import styled from "styled-components";

import { MAP_CONTROL_STACK } from "@ryde/controls";
import { DATA_E2E_KEY, type IStyledAttributes } from "@ryde/shared";
import {
  blueGreyMui,
  deepPurpleMui,
  rydeColors,
  ScreenDepth,
} from "@ryde/theme";

export const StationsGridMapStyled = styled.div.attrs<IStyledAttributes>({
  [DATA_E2E_KEY]: "stations-grid-map-wrapper",
})`
  height: 100%;
  width: 100%;
  position: relative;

  div.map_scrollable_content {
    min-width: max(100%, 250px);
    height: 100%;
  }

  .empty-state {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    padding: 12px 18px;
    background: ${rydeColors.white};
    border: 1px solid ${blueGreyMui._200};
    border-radius: 4px;
    color: ${blueGreyMui._500};
    font-size: 14px;
    text-align: center;
    z-index: ${ScreenDepth.FLOATING_MENU};
    pointer-events: none;
  }
`;

/** Stations map control buttons — mirrors the passengers map `MapButtonsControlsStyled`. */
export const StationsMapControlsStyled = styled.div.attrs<IStyledAttributes>({
  [DATA_E2E_KEY]: "stations-map-controls-wrapper",
})`
  /*
   * Sits directly under the zoom control and has to line up with it. MapLibre's control stack insets 10px
   * from the top-left with 29px-square buttons and a 4px radius, so these match that box rather than the
   * slightly-inset 12px / 3px they used to carry.
   */
  /* Continues MapLibre's own stack: its 10px inset plus the zoom group's two 29px buttons, plus the 10px
     it would put between one control group and the next, plus what the host added (MAP_CONTROL_STACK). */
  top: calc(78px + var(${MAP_CONTROL_STACK.extraHeightVar}, 0px));
  /* @noflip keeps it on the physical left in RTL (Hebrew) too — stylis-plugin-rtl won't flip it. */
  left: 10px /* @noflip */;
  position: absolute;
  z-index: ${ScreenDepth.MAP_ATTRIBUTIONS};
  display: flex;
  flex-direction: column;
  width: fit-content;

  .mapControlBtn {
    cursor: pointer;
    box-sizing: border-box;
    width: 29px;
    height: 29px;
    border-radius: 4px;
    border: 1px solid ${blueGreyMui._200};
    background: ${rydeColors.white};
    padding: 0;
    margin-bottom: 4px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: ${rydeColors.black};

    &.selected {
      color: ${deepPurpleMui.A700};
    }

    &.disabled {
      color: ${blueGreyMui.A200};
      pointer-events: none;
      cursor: default;
    }

    .mapControlBtn-icon {
      width: 20px;
      height: 20px;
    }
  }
`;

/** Top bar panel for choosing the bulk-action target — mirrors the passengers map panels. */
export const StationsMapBulkTargetPanelStyled = styled.div.attrs<IStyledAttributes>(
  {
    [DATA_E2E_KEY]: "stations-map-bulk-target-panel",
  },
)`
  position: absolute;
  top: 0;
  inset-inline: 0;
  padding: 10px 20px;
  background: ${rydeColors.white};
  /* Above the map, but below the right drawers so it never overlaps them (mirrors FLOATING_MENU). */
  z-index: ${ScreenDepth.FLOATING_MENU};
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 12px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.15);

  .panel-content {
    width: 90%;
    display: flex;
    flex-direction: column;
    gap: 10px;
  }

  .panel-title {
    font-size: 14px;
    font-weight: 700;
    color: ${rydeColors.black};
  }

  .panel-row {
    display: flex;
    align-items: center;
    gap: 8px;

    .dropdownInput {
      flex: 1;
      min-width: 0;
      /* Anchor the clear/arrow adornment here (a non-scrolling ancestor) so it stays put. */
      position: relative;

      /* Cap the chips area so a large selection scrolls instead of growing the panel. */
      .MuiOutlinedInput-root {
        max-height: 96px;
        overflow-y: auto;
        align-content: flex-start;
        /* static so the absolute end-adornment anchors to .dropdownInput, not this scroll box. */
        position: static;
        /* Border on the (non-scrolling) box itself so it stays put while chips scroll;
           same color as the default MUI outline. */
        border: 1px solid rgba(0, 0, 0, 0.23);
        border-radius: 4px;

        /* Keep the scrollbar inset from the rounded corners. */
        &::-webkit-scrollbar {
          width: 8px;
        }
        &::-webkit-scrollbar-track {
          margin: 6px 0;
        }
      }

      .MuiOutlinedInput-root:hover {
        border-color: rgba(0, 0, 0, 0.87);
      }

      .MuiOutlinedInput-root.Mui-focused {
        border-color: ${deepPurpleMui.A700};
        border-width: 2px;
      }

      /* Drop the default fieldset outline — it lives inside the scroll area and would scroll. */
      .MuiOutlinedInput-notchedOutline {
        border: none;
      }

      .MuiInputLabel-root.MuiInputLabel-shrink {
        background: ${rydeColors.white};
        padding-inline: 4px;
      }
    }
  }

  .close-icon-svg {
    cursor: pointer;
    flex-shrink: 0;
    margin-inline-start: auto;
  }
`;

export const StationsMapStationInfoPopupStyled = styled.div.attrs<IStyledAttributes>(
  {
    [DATA_E2E_KEY]: "station-map-station-info-container",
  },
)`
  width: 220px;
  padding: 0px;

  .station-info-header {
    border-bottom: 1px solid ${rydeColors.softGray};
    margin-bottom: 5px;
    padding-bottom: 5px;

    .station-name {
      text-align: start;
      color: ${blueGreyMui._500};
      font-size: 12px;
      font-weight: 700;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .area-row {
      display: flex;
      align-items: center;
      gap: 6px;
      margin-top: 4px;
      font-size: 12px;
      color: ${rydeColors.grey};

      .area-label {
        font-weight: 700;
        flex-shrink: 0;
      }

      .polygon-toggle {
        flex-shrink: 0;
        display: inline-flex;
        align-items: center;
        cursor: pointer;

        &.loading {
          opacity: 0.6;
          cursor: progress;
        }

        .polygon-dot {
          box-sizing: border-box;
          width: 12px;
          height: 12px;
          border-radius: 50%;
          border: 1px solid ${rydeColors.softGray};
          background: transparent;
        }

        &.shown .polygon-dot {
          border-color: ${rydeColors.white};
          box-shadow: 0 0 0 1px ${rydeColors.softGray};
        }
      }

      .area-name {
        min-width: 0;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }
    }
  }

  .attributes {
    padding: 6px 0;
    display: flex;
    flex-wrap: wrap;
    gap: 4px 8px;
    font-size: 12px;
    color: ${rydeColors.grey};

    .attribute-chip {
      padding: 5px 6px;
      border-radius: 10px;
      background: ${rydeColors.smoke};
      color: ${rydeColors.white};

      &.isOfficial {
        background: ${deepPurpleMui.A700};
      }
      &.isDestination {
        background: ${rydeColors.brightVividPurple};
      }
    }

    .empty {
      color: ${rydeColors.softGray};
    }
  }

  .footer {
    height: 32px;
    padding: 0 8px;
    gap: 8px;
    display: flex;
    justify-content: center;
    align-items: center;
    border-top: 1px solid ${rydeColors.rowHighlight};

    .edit-button {
      color: ${rydeColors.passInBlue};
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 6px;
    }

    .edit-button.disabled {
      color: ${rydeColors.softGray};
      cursor: not-allowed;
    }
  }
`;
